import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import NewsletterSignup from "../components/NewsletterSignup";

const PageContainer = styled.div`
  padding-top: 70px;
`;

const HeroSection = styled.section`
  background: linear-gradient(
    135deg,
    var(--primary-light) 0%,
    var(--primary-color) 100%
  );
  padding: 6rem 1rem 4rem;
  color: white;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ContentSection = styled.section`
  padding: 5rem 1rem;
  background-color: ${(props) =>
    props.alternate ? "var(--background-alt)" : "var(--background)"};
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  gap: 4rem;

  @media (max-width: 992px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const ContentBlock = styled.div`
  flex: 1;
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  text-align: center;
  padding: 2rem;
  background: var(--background-alt);
  border-radius: 8px;
  margin: 2rem 0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const InfoCard = styled.div`
  background: var(--background-alt);
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const InfoLabel = styled.div`
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
`;

const InfoValue = styled.div`
  color: var(--text-primary);
`;

const MeetingCard = styled.div`
  background: var(--background-alt);
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const MeetingLink = styled.a`
  display: inline-block;
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background: var(--primary-color);
  color: white;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background: var(--primary-dark);
  }
`;

const GroupProfile = () => {
  const { id } = useParams();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { ref: ref1, inView: inView1 } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    const fetchGroup = async () => {
      try {
        const groupDoc = await getDoc(doc(db, "groups", id));

        if (!groupDoc.exists()) {
          setError("Group not found");
          setGroup(null);
          return;
        }

        const groupData = {
          id: groupDoc.id,
          ...groupDoc.data(),
        };

        setGroup(groupData);
        setError(null);
      } catch (err) {
        console.error("Error fetching group:", err);
        setError("Unable to load group information. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchGroup();
  }, [id]);

  if (loading) {
    return (
      <PageContainer>
        <ContentSection>
          <ContentContainer>
            <ContentBlock>
              <h2>Loading...</h2>
            </ContentBlock>
          </ContentContainer>
        </ContentSection>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ContentSection>
          <ContentContainer>
            <ContentBlock>
              <ErrorMessage>
                <h2>Error</h2>
                <p>{error}</p>
              </ErrorMessage>
            </ContentBlock>
          </ContentContainer>
        </ContentSection>
      </PageContainer>
    );
  }

  if (!group) {
    return (
      <PageContainer>
        <ContentSection>
          <ContentContainer>
            <ContentBlock>
              <ErrorMessage>
                <h2>Group Not Found</h2>
                <p>The requested group could not be found.</p>
              </ErrorMessage>
            </ContentBlock>
          </ContentContainer>
        </ContentSection>
      </PageContainer>
    );
  }

  const formatDate = (date) => {
    if (!date) return "N/A";
    if (typeof date === "string") return new Date(date).toLocaleDateString();
    return date.toDate().toLocaleDateString();
  };

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <PageTitle>{group.name}</PageTitle>
          <PageSubtitle>{group.type} Group</PageSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <ContentContainer
          ref={ref1}
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={inView1 ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <ContentBlock>
            <h2>About This Group</h2>
            <p>{group.description}</p>

            <InfoGrid>
              <InfoCard>
                <InfoLabel>Location</InfoLabel>
                <InfoValue>
                  {group.placeName || group.location}
                  {group.address && <div>{group.address}</div>}
                  {group.city && group.state && (
                    <div>
                      {group.city}, {group.state} {group.zip}
                    </div>
                  )}
                </InfoValue>
              </InfoCard>

              <InfoCard>
                <InfoLabel>Members</InfoLabel>
                <InfoValue>{group.memberCount}</InfoValue>
              </InfoCard>

              <InfoCard>
                <InfoLabel>Founded</InfoLabel>
                <InfoValue>{formatDate(group.foundedDate)}</InfoValue>
              </InfoCard>

              <InfoCard>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  {group.isClaimed ? "Verified Group" : "Unverified Group"}
                </InfoValue>
              </InfoCard>
            </InfoGrid>
          </ContentBlock>
        </ContentContainer>
      </ContentSection>

      {/* <ContentSection alternate>
        <ContentContainer>
          <ContentBlock>
            <h2>Meeting Schedule</h2>
            {group.meetings &&
              group.meetings.map((meeting, index) => (
                <MeetingCard key={index}>
                  <h3>
                    {meeting.day} at {meeting.time}
                  </h3>
                  <p>{meeting.format}</p>
                  <p>{meeting.locationName}</p>
                  {meeting.online && meeting.meetingLink && (
                    <MeetingLink
                      href={meeting.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Join Online Meeting
                    </MeetingLink>
                  )}
                </MeetingCard>
              ))}
          </ContentBlock>
        </ContentContainer>
      </ContentSection> */}

      <NewsletterSignup />
    </PageContainer>
  );
};

export default GroupProfile;
